/**
 * Monaco Editor 主题管理Hook
 *
 * 提供主题切换、系统主题检测、主题持久化等功能
 */

import * as monaco from 'monaco-editor'
import { useCallback, useEffect, useState } from 'react'
import { MONACO_THEMES } from '@/constants/monaco-config'
import { applyTheme, getAvailableThemes, getMonacoThemeName, getSystemThemePreference, initializeThemes } from '@/utils/monaco-themes'

export type ThemePreference = 'light' | 'dark' | 'system'
export type MonacoTheme = keyof typeof MONACO_THEMES

export interface ThemeInfo {
  name: string
  label: string
  description: string
  isCustom?: boolean
}

export interface UseMonacoThemeReturn {
  /** 当前主题偏好 */
  preference: ThemePreference
  /** 当前Monaco主题 */
  currentTheme: string
  /** 系统主题偏好 */
  systemPreference: 'light' | 'dark'
  /** 是否为深色模式 */
  isDark: boolean
  /** 可用主题列表 */
  availableThemes: ThemeInfo[]
  /** 设置主题偏好 */
  setPreference: (preference: ThemePreference) => void
  /** 切换Monaco主题 */
  setMonacoTheme: (theme: string) => void
  /** 切换深色/浅色模式 */
  toggleDarkMode: () => void
  /** 重置为系统主题 */
  resetToSystemTheme: () => void
  /** 应用主题到Monaco实例 */
  applyToEditor: (editor: monaco.editor.IStandaloneCodeEditor) => void
}

/**
 * 本地存储键名
 */
const STORAGE_KEYS = {
  // biome-ignore lint/style/useNamingConvention: <''>
  THEME_PREFERENCE: 'monaco-theme-preference',
  // biome-ignore lint/style/useNamingConvention: <''>
  MONACO_THEME: 'monaco-editor-theme',
} as const

/**
 * Monaco Editor 主题管理Hook
 */
export const useMonacoTheme = (): UseMonacoThemeReturn => {
  // 从本地存储加载偏好设置
  const getStoredPreference = (): ThemePreference => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEYS.THEME_PREFERENCE) as ThemePreference
      return stored || 'system'
    }
    return 'system'
  }

  const getStoredMonacoTheme = (): string => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.MONACO_THEME) || MONACO_THEMES.dark
    }
    return MONACO_THEMES.dark
  }

  const [preference, setPreferenceState] = useState<ThemePreference>(getStoredPreference())
  const [currentTheme, setCurrentTheme] = useState<string>(getStoredMonacoTheme())
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>(getSystemThemePreference())
  const [availableThemes, setAvailableThemes] = useState<ThemeInfo[]>([])

  // 计算当前是否为深色模式
  const isDark = preference === 'system' ? systemPreference === 'dark' : preference === 'dark'

  // 初始化主题系统
  useEffect(() => {
    initializeThemes()
    setAvailableThemes(getAvailableThemes())
  }, [])

  // 监听系统主题变化
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      const handleChange = (e: MediaQueryListEvent) => {
        const newSystemPreference = e.matches ? 'dark' : 'light'
        setSystemPreference(newSystemPreference)

        // 如果用户偏好是系统，则更新主题
        if (preference === 'system') {
          const themeName = getMonacoThemeName('system')
          applyTheme(themeName)
          setCurrentTheme(themeName)
        }
      }

      // 初始化系统主题偏好
      setSystemPreference(mediaQuery.matches ? 'dark' : 'light')

      // 添加监听器
      mediaQuery.addEventListener('change', handleChange)

      return () => {
        mediaQuery.removeEventListener('change', handleChange)
      }
    }
  }, [preference])

  // 应用主题
  useEffect(() => {
    let themeName: string

    if (preference === 'system') {
      themeName = getMonacoThemeName('system')
    } else {
      themeName = getMonacoThemeName(preference)
    }

    applyTheme(themeName)
    setCurrentTheme(themeName)
  }, [preference])

  // 保存偏好设置到本地存储
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.THEME_PREFERENCE, preference)
    }
  }, [preference])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.MONACO_THEME, currentTheme)
    }
  }, [currentTheme])

  // 设置主题偏好
  const setPreference = useCallback((newPreference: ThemePreference) => {
    setPreferenceState(newPreference)

    // 计算应该应用的主题
    let themeName: string
    if (newPreference === 'system') {
      themeName = getMonacoThemeName('system')
    } else {
      themeName = getMonacoThemeName(newPreference)
    }

    applyTheme(themeName)
    setCurrentTheme(themeName)
  }, [])

  // 设置Monaco主题
  const setMonacoTheme = useCallback(
    (theme: string) => {
      applyTheme(theme)
      setCurrentTheme(theme)

      // 如果主题是内置主题，更新偏好设置
      const _isDarkTheme = theme.includes('dark')
      if (theme === MONACO_THEMES.vs || theme === MONACO_THEMES.light) {
        setPreference('light')
      } else if (theme === MONACO_THEMES['vs-dark'] || theme === MONACO_THEMES.dark) {
        setPreference('dark')
      }
    },
    [setPreference],
  )

  // 切换深色/浅色模式
  const toggleDarkMode = useCallback(() => {
    const newPreference: ThemePreference = isDark ? 'light' : 'dark'
    setPreference(newPreference)
  }, [isDark, setPreference])

  // 重置为系统主题
  const resetToSystemTheme = useCallback(() => {
    setPreference('system')
  }, [setPreference])

  // 应用主题到Monaco编辑器实例
  const applyToEditor = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      try {
        monaco.editor.setTheme(currentTheme)

        // 更新编辑器选项以适应主题
        const currentOptions = editor.getOptions()
        const _isDarkMode = currentTheme.includes('dark')

        // 根据主题调整编辑器配置
        editor.updateOptions({
          ...currentOptions,
          // 可以根据主题调整其他选项
          minimap: {
            enabled: currentOptions.get(monaco.editor.EditorOption.minimap)?.enabled,
          },
        })

        console.debug(`Applied theme ${currentTheme} to editor`)
      } catch (error) {
        console.error(`Failed to apply theme ${currentTheme} to editor:`, error)
      }
    },
    [currentTheme],
  )

  return {
    preference,
    currentTheme,
    systemPreference,
    isDark,
    availableThemes,
    setPreference,
    setMonacoTheme,
    toggleDarkMode,
    resetToSystemTheme,
    applyToEditor,
  }
}

/**
 * 获取主题图标的Hook
 */
export const useThemeIcon = (isDark: boolean): string => {
  return isDark ? '🌙' : '☀️'
}

/**
 * 获取主题样式的Hook
 */
export const useThemeStyles = (isDark: boolean) => {
  return {
    container: isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900',
    border: isDark ? 'border-gray-700' : 'border-gray-300',
    background: isDark ? 'bg-gray-800' : 'bg-gray-50',
    text: isDark ? 'text-gray-100' : 'text-gray-900',
    subtext: isDark ? 'text-gray-400' : 'text-gray-600',
    hover: isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100',
    active: isDark ? 'bg-gray-700' : 'bg-gray-200',
    focus: isDark ? 'focus:ring-blue-500' : 'focus:ring-blue-400',
  }
}
